import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import useFetch from "hooks/useFetch";
import { getToken } from "utils/getToken";
import { useEffect, useState } from "react";
import { IUser } from "interfaces/auth";
import CustomSearchSelect from "./CustomSearchSelect";
import CustomInput from "components/Input";
import CustomButton from "components/Button";
import { toast } from "react-toastify";
import IndividualNotificationsTab from "./IndividualNotificationsTab";
import { INotification } from "interfaces/Notifications";

type ISelectedUser = { label: string; value: string; icon: string | null };

const IndividualNotifications = () => {
  const token = getToken();
  const { t } = useTranslation();
  const [usersArr, setUsersArr] = useState<IUser[]>([]);
  const [individualNotifiArr, setIndividualNotifiArr] = useState<INotification[]>([]);
  const [selectedUsersIds, setSelectedUsersIds] = useState<string[] | null>(null);
  const [notificationText, setNotificationText] = useState<string>("");
  const options = usersArr.map((user) => {
    return {
      label: user.email || user.telegram || user.telegramUsername,
      value: user._id,
      icon: user.icon || null,
    };
  });

  const { triggerFetch: getAllUsers } = useFetch({
    url: "admin/users",
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const { triggerFetch: getIndividualNotifications } = useFetch({
    url: "admin/notifications/individual-notification",
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const { loading: isCreateNotifiLoading, triggerFetch: createIndividualNotification } = useFetch({
    url: "admin/notifications/individual-notification",
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const { triggerFetch: deleteIndividualNotification } = useFetch({
    url: `admin/notifications/individual-notification`,
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const handleGetAllUsers = async () => {
    const { users } = await getAllUsers();
    setUsersArr(users);
  };

  const handleGetAllIndividualNotifications = async () => {
    const { data } = await getIndividualNotifications();
    setIndividualNotifiArr(data.reverse() || []);
  };

  const handleSelectUsers = (selectedUsersArr: ISelectedUser[] | null) => {
    if (!selectedUsersArr) {
      setSelectedUsersIds(null);
      return;
    }

    const selectedUsersIds = selectedUsersArr.map((user) => user.value);
    setSelectedUsersIds(selectedUsersIds);
  };

  const handleCreateButtonClick = async () => {
    if (!notificationText) {
      toast.error(t("notificationTextError"));
      return;
    }

    if (!selectedUsersIds || !selectedUsersIds.length) {
      toast.error(t("notificationUsersArrError"));
      return;
    }

    try {
      const result = await createIndividualNotification({
        text: notificationText,
        users: selectedUsersIds,
      });
      if (result.success) {
        handleGetAllIndividualNotifications();
        toast.success(t("successfulIndividualNotificationCreation"));
      } else {
        toast.error(t("unsuccessfulGIndividualNotificationCreation"));
      }
    } catch (error) {
      toast.error(t("unsuccessfulGIndividualNotificationCreation"));
    }
  };

  const handleIndividualNotificationDelete = async (id: string) => {
    if (!id) {
      return;
    }
    try {
      await deleteIndividualNotification({ id });
      await handleGetAllIndividualNotifications();
      toast.success(t("successfulIndividualNotificationDeleted"));
    } catch (error) {
      toast.error(t("unsuccessfulIndividualNotificationDeleted"));
    }
  };

  useEffect(() => {
    void handleGetAllUsers();
    void handleGetAllIndividualNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Typography
        sx={{
          fontFamily: "var(--cygreMedium), sans-serif",
          color: "#2E263D",
          fontSize: "18px",
          lineHeight: "32px",
          margin: "24px 0px 24px 0px",
        }}
      >
        {t("individualNotifications")}
      </Typography>
      <Typography
        sx={{
          fontFamily: "var(--cygreMedium), sans-serif",
          color: "#2E263D",
          fontSize: "16px",
          lineHeight: 1.22,
          textAlign: "center",
          marginBottom: "24px",
        }}
      >
        {t("createIndividualNotification")}
      </Typography>
      <Box sx={{ marginBottom: "40px" }}>
        <CustomInput
          sx={{ marginBottom: "24px" }}
          required
          fullWidth
          id="notification-text"
          placeholder={t("enterNotificationText")}
          multiline
          rows={4}
          label={t("notificationText")}
          value={notificationText}
          onChange={(event) => {
            setNotificationText(event.target.value);
          }}
          customBgColor="#F6F7FB"
        />
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: "10px" }}>
          <CustomSearchSelect options={options.reverse()} onChange={handleSelectUsers} />
          <CustomButton
            textButton={t("create")}
            height={40}
            width={"100%"}
            customBgColor="#F4701B"
            customHoverColor="#F4701B"
            customBorderRadius="8px"
            customFontSize="14px"
            customTextTransform="none"
            onClick={handleCreateButtonClick}
            loading={isCreateNotifiLoading}
          />
        </Box>
      </Box>
      {individualNotifiArr.length ? (
        <IndividualNotificationsTab
          individualNotifications={individualNotifiArr}
          deleteIndividualNotification={handleIndividualNotificationDelete}
        />
      ) : null}
    </>
  );
};

export default IndividualNotifications;
